import { useState, useCallback, useMemo } from 'react';

/**
 * Hook for managing table sort state and handlers
 * @param {Object} options - Configuration options
 * @param {string} options.order - Initial sort order (column id)
 * @param {boolean} options.reverse - Initial reverse state
 * @param {Array} options.columns - Column configuration array
 * @param {Function} options.sortCB - Callback when sort changes
 * @returns {Object} Sort state and handlers
 */
const useTableSort = ({ order, reverse = false, columns = [], sortCB }) => {
  const sortOrderOrig = useMemo(() => order || (columns[0] && columns[0].id) || '', [order, columns]);
  const sortReverseOrig = useMemo(() => reverse, [reverse]);

  const [sortConfig, setSortConfig] = useState({
    order: sortOrderOrig,
    reverse: reverse,
    click: 0,
  });

  const handleClickColumn = useCallback(
    (column) => {
      if (column.sort === false) return;

      setSortConfig((prev) => {
        let newConfig = { ...prev };

        if (prev.order === column.id) {
          newConfig.click = prev.click + 1;

          if (newConfig.click === 1) {
            // First click: reverse
            newConfig.reverse = !prev.reverse;
          } else {
            // Second click: revert to default
            newConfig.order = sortOrderOrig;
            newConfig.reverse = sortReverseOrig;
            newConfig.click = 0;
          }
        } else {
          // New column
          newConfig.click = 0;
          newConfig.order = column.id;
          newConfig.reverse = false;
        }

        return newConfig;
      });

      if (sortCB) {
        sortCB(column);
      }
    },
    [sortCB, sortOrderOrig, sortReverseOrig]
  );

  return {
    sortConfig,
    setSortConfig,
    handleClickColumn,
    sortOrderOrig,
    sortReverseOrig,
  };
};

export default useTableSort;
